import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { deleteActivity } from "@/services/activity/activityService";
import { ActivityWithRelations } from "@/types/custom-types";
import { Loader2, CalendarDays, Users, Building2, Plus, Pencil, Trash2, Eye } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ActionMenu } from "@/components/ui/action-menu";
import { DeleteConfirmation } from "@/components/ui/delete-confirmation";

export default function Activities() {
  const [activities, setActivities] = useState<ActivityWithRelations[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [activityToDelete, setActivityToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("activities")
        .select(`
          *,
          organization:organizations(*),
          contact:contacts(*)
        `)
        .order("date", { ascending: false });

      if (error) {
        throw error;
      }

      setActivities(data || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load activities",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivities();
  }, [toast]);
  
  const handleDeleteActivity = async () => {
    if (!activityToDelete) return;
    
    try {
      setIsDeleting(true);
      await deleteActivity(activityToDelete);
      toast({
        title: "Success",
        description: "Activity deleted successfully",
      });
      await fetchActivities();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete activity",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setActivityToDelete(null);
    }
  };

  const activityTypes = Array.from(
    new Set(activities.map((act) => act.type))
  ).filter(Boolean);

  const filteredActivities = activities.filter(activity => 
    (activity.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    activity.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    activity.organization?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (activity.contact && `${activity.contact.first_name} ${activity.contact.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()))) &&
    (typeFilter === "all" ? true : activity.type === typeFilter)
  );

  const formatDateTime = (dateString: string | null | undefined) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <Skeleton className="h-8 w-32" />
          <div className="flex items-center gap-4 w-full sm:w-auto">
            <Skeleton className="h-9 w-[180px]" />
            <Skeleton className="h-9 w-64" />
            <Skeleton className="h-9 w-32" />
          </div>
        </div>
        
        <Card>
          <CardHeader className="pb-2">
            <Skeleton className="h-6 w-28" />
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center space-x-4 p-3 border rounded-lg">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-36" />
                  <Skeleton className="h-6 w-16" />
                  <Skeleton className="h-8 w-8 rounded" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold tracking-tight">Activities</h1>
        <div className="flex items-center gap-4 w-full sm:w-auto">
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {activityTypes.map((type) => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative w-full sm:w-64">
              <Input
                placeholder="Search activities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>
          </div>
          <Button onClick={() => navigate("/activities/create")}>
            <Plus className="h-4 w-4 mr-2" />
            New Activity
          </Button>
        </div>
      </div>

      {filteredActivities.length === 0 ? (
        <Card>
          <CardContent className="pt-8 pb-8 text-center">
            <div className="flex flex-col items-center space-y-4">
              <CalendarDays className="h-12 w-12 text-muted-foreground/50" />
              <div className="space-y-2">
                <h3 className="text-lg font-medium">
                  {searchTerm || typeFilter !== "all" ? "No activities match your search" : "No activities yet"}
                </h3>
                <p className="text-muted-foreground text-sm max-w-sm">
                  {searchTerm || typeFilter !== "all" 
                    ? "Try adjusting your search terms or clearing filters to see more results."
                    : "Start tracking your client interactions by creating your first activity. Activities help you log meetings, calls, emails, and other important touchpoints."
                  }
                </p>
              </div>
              {!searchTerm && typeFilter === "all" && (
                <Button onClick={() => navigate("/activities/create")} className="mt-2">
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Activity
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle>All Activities</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead className="hidden md:table-cell">Date & Time</TableHead>
                  <TableHead className="hidden lg:table-cell">Contact</TableHead>
                  <TableHead className="hidden md:table-cell">Organization</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredActivities.map((activity) => (
                  <TableRow key={activity.id} className="hover:bg-muted/50">
                    <TableCell className="font-medium">
                      {activity.title}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="flex items-center">
                        <CalendarDays className="h-4 w-4 mr-2 text-muted-foreground" />
                        {formatDateTime(activity.date)}
                      </div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {activity.contact ? (
                        <div className="flex items-center">
                          <Users className="h-4 w-4 mr-2 text-muted-foreground" />
                          {`${activity.contact.first_name} ${activity.contact.last_name}`}
                        </div>
                      ) : "—"}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {activity.organization ? (
                        <div className="flex items-center">
                          <Building2 className="h-4 w-4 mr-2 text-muted-foreground" />
                          {activity.organization.name}
                        </div>
                      ) : "—"}
                    </TableCell>
                    <TableCell>
                      <ActivityTypeBadge type={activity.type} />
                    </TableCell>
                    <TableCell className="text-right">
                      <ActionMenu
                        items={[
                          {
                            label: "View Details",
                            icon: <Eye className="h-4 w-4 mr-2" />,
                            onClick: () => {
                              // For now just show a toast as we don't have a details page yet
                              toast({
                                title: activity.title,
                                description: activity.description || "No description provided",
                              });
                            }
                          },
                          {
                            label: "Edit",
                            icon: <Pencil className="h-4 w-4 mr-2" />,
                            href: `/activities/${activity.id}/edit`
                          },
                          {
                            label: "Delete",
                            icon: <Trash2 className="h-4 w-4 mr-2" />,
                            onClick: () => setActivityToDelete(activity.id),
                            variant: "destructive"
                          }
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
      
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmation
        isOpen={!!activityToDelete}
        onClose={() => setActivityToDelete(null)}
        onConfirm={handleDeleteActivity}
        entityName="activity"
      />
    </div>
  );
}

type ActivityTypeProps = {
  type: string;
};

function ActivityTypeBadge({ type }: ActivityTypeProps) {
  let variant: "default" | "outline" | "secondary" = "secondary";
  
  switch (type.toLowerCase()) {
    case 'meeting':
      variant = "default";
      break;
    case 'call':
      variant = "outline";
      break;
    case 'email':
      variant = "secondary";
      break;
    default:
      variant = "secondary";
  }

  return (
    <Badge variant={variant}>
      {type}
    </Badge>
  );
}
